import React from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Row, Col, Checkbox } from "antd";

import { fechaCierre as horaRealDeCierre } from './horaCierre';
import { MiniaturaConvocatoria } from '../components/Generales/MiniaturaConvocatoria';

/**
 * Los anchos de la tabla de convocatorias, en un solo sitio.
 *
 * Sin un scroll horizontal declarado antd reparte el espacio segun el
 * contenido y el width queda en sugerencia: Tipo participante, que es una
 * sola linea larga, se llevaba mas que Nombre y Linea, que son las que mas
 * texto tienen, y esas dos quedaban angostas y la fila altisima.
 */
export const ANCHOS_TABLA_CONVOCATORIA = {
  portada: 90,
  nombre: 240,
  linea: 300,
  apertura: 110,
  cierre: 110,
  participante: 190,
  estado: 90,
  entidad: 90,
  area: 200,
};

/** Lo que suman. Declararlo es lo que hace que los anchos se respeten. */
export const ANCHO_TABLA_CONVOCATORIA = Object.values(
  ANCHOS_TABLA_CONVOCATORIA
).reduce((total, ancho) => total + ancho, 0);

/**
 * La portada, lo primero de la fila.
 *
 * Es con lo que empezaba la tarjeta, y al pasar la tabla al frente las
 * filas se quedaban de puro texto, donde antes la convocatoria se
 * reconocia por la imagen.
 */
export const columnaMiniatura = () => ({
  title: "",
  width: ANCHOS_TABLA_CONVOCATORIA.portada,
  key: "portada",
  render: (datos: any) => (
    <MiniaturaConvocatoria
      portadaUrl={datos?.portada_url}
      nombre={datos?.nombre_convocatoria}
    />
  ),
});

export const obtenerSelectCheckboxColumn = (
  datosFiltro: any[],
  toggleSeleccionarTodos: () => void,
  cambiarCheckbox: (datos: any) => void,
  width: number,
  device: string
) => {
  return {
    title: () => (
      <Row className="width-100">
        <Col span={24} className="justify-content-center">
          Seleccionar todos{" "}
          <Checkbox
            checked={datosFiltro.length > 0 && datosFiltro.every((d: any) => d.checked)}
            indeterminate={
              datosFiltro.some((d: any) => d.checked) &&
              !datosFiltro.every((d: any) => d.checked)
            }
            onChange={toggleSeleccionarTodos}
          />
        </Col>
      </Row>
    ),
    width: width,
    fixed: device === "lg" ? "right" : false,
    render: (datos: any) => (
      <Row className="width-100">
        <Col span={24} className="justify-content-center">
          <Checkbox
            checked={datos.checked}
            onChange={() => cambiarCheckbox(datos)}
          />
        </Col>
      </Row>
    ),
  };
};

export const handleFormFieldsChange = (
  changedField: any,
  principalState: any,
  setPrincipalState: any
) => {
  const infoupdate = { ...principalState.buscarconvocatorias };
  infoupdate[changedField[0].name[0]] = changedField[0].value;
  setPrincipalState({ ...principalState, buscarconvocatorias: infoupdate });
};

export const handleSimpleFieldsChange = (
  changedField: any,
  state: any,
  setState: any,
  ignoreField?: string
) => {
  if (!changedField || changedField.length === 0) return;
  const fieldName = changedField[0].name[0];
  if (ignoreField && fieldName === ignoreField) return;
  const infoupdate = { ...state };
  infoupdate[fieldName] = changedField[0].value;
  setState(infoupdate);
};

export const getFilterLineaConvocatoria = (
  idLineaConvocatoria: number,
  data: any,
  LineasConvocatorias: any[]
) => {
  const valueFilter = LineasConvocatorias.filter((x) => x.id === idLineaConvocatoria);
  if (valueFilter[0] !== undefined && valueFilter[0].nombre !== undefined) {
    return valueFilter[0].nombre;
  } else if (data.nombre_convocatoria?.includes("jurados")) {
    return "Banco de Jurados";
  } else {
    return "BEPS";
  }
};

export const obtenerColumnsTablaConvocatoria = (
  LineasConvocatorias: any[],
  coloresEstado: any,
  isAgenda: boolean
) => {
  return [
    columnaMiniatura(),
    {
      title: "Nombre convocatoria",
      width: ANCHOS_TABLA_CONVOCATORIA.nombre,
      dataIndex: "nombre_convocatoria",
      key: "nombre_convocatoria",
      render: (datos: any) => {
        return <span style={{ whiteSpace: "pre-line" }}>{datos}</span>;
      },
    },
    {
      title: "Línea convocatoria",
      width: ANCHOS_TABLA_CONVOCATORIA.linea,
      render: (datos: any) => {
        return (
          <span style={{ fontFamily: "Montserrat-Regular", whiteSpace: "pre-line", fontWeight: "bold" }}>
            {getFilterLineaConvocatoria(datos?.linea_convocatoria, datos, LineasConvocatorias) !== undefined
              ? `${getFilterLineaConvocatoria(datos?.linea_convocatoria, datos, LineasConvocatorias)}`
              : ""}
          </span>
        );
      },
    },
    {
      title: "Fecha apertura",
      width: ANCHOS_TABLA_CONVOCATORIA.apertura,
      render: (datos: any) => {
        let fecha = datos.fechas;
        if (Array.isArray(datos.fechas)) {
          let fechaapertura = datos.fechas.filter((data: any) => data.clave === "Apertura");
          if (fechaapertura.length > 0) {
            fecha = fechaapertura[0].valormin.substring(0, 10);
          }
        }
        return <span className="font-family-Montserrat-Regular">{fecha}</span>;
      },
    },
    {
      title: "Fecha cierre",
      width: ANCHOS_TABLA_CONVOCATORIA.cierre,
      render: (datos: any) => {
        let fecha = datos.fechas;
        if (Array.isArray(datos.fechas)) {
          let fechacierre = datos.fechas.filter((data: any) => data.clave === "Cierre");
          if (fechacierre.length > 0) {
            fecha = fechacierre[0].valormin.substring(0, 10);
          }
        }
        return <span className="font-family-Montserrat-Regular">{fecha}</span>;
      },
    },
    {
      title: "Tipo participante",
      width: ANCHOS_TABLA_CONVOCATORIA.participante,
      render: (datos: any) => {
        // Unidos, no con un separador colgando al final de cada uno.
        const tipo_participante = Array.isArray(datos.tipo_participante)
          ? datos.tipo_participante.map((tipo: any) => tipo.text).join(" - ")
          : "";
        return (
          <span className="font-family-Montserrat-Regular" style={{ whiteSpace: "pre-line" }}>
            {tipo_participante}
          </span>
        );
      },
    },
    {
      title: "Estado",
      width: ANCHOS_TABLA_CONVOCATORIA.estado,
      render: (datos: any) => {
        let fecha = datos.fechas;
        let estadoAct = datos.estado;
        if (Array.isArray(datos.fechas)) {
          let fechaCierre = datos.fechas.filter((data: any) => data.clave === "Cierre");

          if (fechaCierre.length > 0) {
            fecha = fechaCierre[0].valormax;

            let fechaActual = new Date();
            let anio = fechaActual.getFullYear();
            let mes = ("0" + (fechaActual.getMonth() + 1)).slice(-2);
            let dia = ("0" + fechaActual.getDate()).slice(-2);
            let hora = ("0" + fechaActual.getHours()).slice(-2);
            let minutos = ("0" + fechaActual.getMinutes()).slice(-2);

            let fechaYHora = anio + "-" + mes + "-" + dia + " " + hora + ":" + minutos;

            let cierreAct = horaRealDeCierre(fecha);
            let anioc = cierreAct.getFullYear();
            let mesc = ("0" + (cierreAct.getMonth() + 1)).slice(-2);
            let diac = ("0" + cierreAct.getDate()).slice(-2);
            let horac = ("0" + cierreAct.getHours()).slice(-2);
            let minutosc = ("0" + cierreAct.getMinutes()).slice(-2);
            let fechaYHoraActualizada =
              anioc + "-" + mesc + "-" + diac + " " + horac + ":" + minutosc;

            if (fechaYHoraActualizada <= fechaYHora) {
              estadoAct = "Cerrada";
            }
          }
        }
        return (
          <span style={{ color: coloresEstado[estadoAct], marginLeft: "0" }}>
            {estadoAct}
          </span>
        );
      },
    },
    {
      title: "Entidad",
      width: ANCHOS_TABLA_CONVOCATORIA.entidad,
      dataIndex: "entidad",
      key: "entidad",
    },
    {
      title: isAgenda ? "Expresiones" : "Expresión cultural",
      width: ANCHOS_TABLA_CONVOCATORIA.area,
      render: (datos: any) => {
        if (Array.isArray(datos.area)) {
          let area = "";
          datos.area.forEach((tipo: any, index: number) => {
            area += tipo.text;
            if (index < datos.area.length - 1) {
              area += ", ";
            } else {
              area += ".";
            }
          });
          return (
            <span className="font-family-Montserrat-Regular" style={{ whiteSpace: "pre-line" }}>
              {area}
            </span>
          );
        }
        return null;
      },
    },
  ];
};

export const itemRenderTable = (current: any, type: any, originalElement: any) => {
  if (type === "prev") {
    return (
      <span className="border-ant-prev-table font-size-10px">
        <LeftOutlined
          className="font-color-051923bf"
          style={{ display: "inline-flex", paddingRight: "2px" }}
        />
        Ant.
      </span>
    );
  }
  if (type === "next") {
    return (
      <span className="border-ant-prev-table font-size-10px">
        Sig.
        <RightOutlined
          className="font-color-051923bf"
          style={{ display: "inline-flex", paddingLeft: "2px" }}
        />
      </span>
    );
  }
  return originalElement;
};
